import { core, type Json } from '../core/index.js';
import { getCurrentPages } from './get-current-pages.js';
/**
 * 跨 `webview` 异步调用任何webview的数据内容, 并获取返回的回调结果
 * Note: 注意当使用sendCrossWebviewCall通知其他webview执行H5注册的回调的时候, 如果当前webview需要退出, 则务必使用sendCrossWebviewCall的needClose 来控制
 * 不能在sendCrossWebviewCall 通过使用needClose=false, 来执行通知, 同时执行webviewback()主动关闭当前webview这样操作可能会存在时机问题导致执行结果不符合预期.
 */
export type FabricCrossWebviewCallOptions<T extends Json> = {
  /**
   * 返回的页面数，如果 delta 大于现有页面数，则返回到首页, 可以通过 `getCurrentPages()`返回所有的栈
   * e.g. A->B->C->D (当前焦点webview为D)
   * delta为正值(+1)表示当前webview往前面(D->A)的webview通讯, delta为负值(-1),表示当前webview往后面(A->D)的webview通讯.
   * @default +1
   */
  delta: number;
  /**
   * 当前delta为正的时候并且超出了所有的webview栈个数, 我们处理方式, 如果needClose=true我们是关闭所有, 但是有的业务场景希望, 这个时候只关闭当前webview
   * 可以通过这个设置为返回 `1`
   */
  overflowDelta?: number;
  /**
   * 当且仅当delta为正的值的时候, 往前面的webview通讯的时候才有是否关闭通讯webview之间的其他webview (D -> A), 默认关闭B,C
   * @default true
   */
  needClose?: boolean;
  /**
   * 被调用的webview栈的web注册好的方法名字
   */
  method: string;
  /**
   * 传递给被调用webview栈的web注册方法的数据内容
   */
  data: T;
};

export type FabricWebviewCrossWebviewCallApi = <Input extends Json>(
  options: FabricCrossWebviewCallOptions<Input>
) => void;

/**
 * 跨 `webview` 异步调用任何webview的数据内容, 并获取返回的回调结果
 * @example
 * ```ts
 * // webview1
 * useEffect(() => {
 *   ready((fabric) => {
 *     console.log('fabric is ready: ', fabric);
 *     fabric.registerSync('webview_wait_callback', function (args) {
 *       console.log(JSON.parse(args));
 *       return {
 *         code: '0',
 *         data: '',
 *       };
 *     });
 *   });
 * });
 * // invoke in webview2
 * sendCrossWebviewCall({
 *   // indicates `webview1`
 *   delta:0,
 *   method: 'webview_wait_callback',
 *   data: { name:'title' }
 * });
 * ```
 * @param options FabricCrossWebviewCallOptions<T extends Json>
 */
export const sendCrossWebviewCall: FabricWebviewCrossWebviewCallApi = <
  Input extends Json,
>(
  options: FabricCrossWebviewCallOptions<Input>
) => {
  let delta = options.delta;
  if (options.overflowDelta) {
    try {
      const totalPages = getCurrentPages();
      if (totalPages < delta) {
        delta = options.overflowDelta;
      }
    } catch {
      // empty block
    }
  }
  core.call<
    'fabric_webview.crossWebviewCall',
    FabricCrossWebviewCallOptions<string>
  >('fabric_webview.crossWebviewCall', {
    ...options,
    delta,
    needClose: options.delta < 0 ? false : options.needClose,
    // Note 此处data因为是已经序列化成字符串了,单Native需要将此字符串直接callWeb因为类型是字符串,所以Native(ios/android)在callHandler
    // 调用web register的回调函数的时候, 不会进行二次序列化, 意味着我们在register回调函数接收到的数据, 就已经是经过了 `parse.jsonParse(data)`
    // 最终结果已经和我们传递的数据类型保持了一致.
    data: JSON.stringify(options.data),
  });
};
